import React, { useMemo, useState, useEffect } from "react";
import DeltaMathTable from "../../../shared/DeltaMathTable";
import { EyeIcon, SwitchHorizontalIcon } from "@heroicons/react/outline";
import AssignmentDiffViewer from "./DiffViewer";
import AssignmentFullViewModal from "./DiffViewModal";
import AssignmentCompareModal from "./DiffCompareModal";

type TeacherAssignmentHistoryAction =
  | "delete"
  | "restore"
  | "create"
  | "update"
  | "initial";
type AssignmentHistoryDiff = {
  action: TeacherAssignmentHistoryAction;
  assignment: Record<string, unknown>;
  createdAt: Date;
};
type AssignmentHistoryDiffsProps = {
  diffs: AssignmentHistoryDiff[];
};

const AssignmentHistoryDiffs: React.FC<AssignmentHistoryDiffsProps> = ({
  diffs,
}) => {
  const prevAssignments = useMemo(() => {
    const arr: Array<Record<string, unknown> | null> = [];
    for (let i = 0; i < diffs.length; ++i) {
      arr[i] =
        i < diffs.length - 1
          ? (diffs[i + 1]?.assignment as Record<string, unknown>)
          : null;
    }
    return arr;
  }, [diffs]);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalAssignment, setModalAssignment] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [modalDate, setModalDate] = useState<Date | undefined>(undefined);

  const openModal = (assignment: Record<string, unknown>, date?: Date) => {
    setModalAssignment(assignment);
    setModalDate(date);
    setModalOpen(true);
  };

  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [compareModalOpen, setCompareModalOpen] = useState(false);

  useEffect(() => {
    setSelectedRows([]);
  }, [diffs]);

  const handleCheckboxChange = (idx: number) => {
    setSelectedRows((prev) => {
      if (prev.includes(idx)) {
        return prev.filter((i) => i !== idx);
      } else {
        if (prev.length === 2) {
          // Only allow two selections, replace the first
          return [prev[1], idx];
        }
        return [...prev, idx];
      }
    });
  };

  const getCompareIndices = () => {
    if (selectedRows.length === 2) return selectedRows;
    if (selectedRows.length === 1 && selectedRows[0] !== 0)
      return [selectedRows[0], 0];
    return null;
  };

  const compareIndices = getCompareIndices();
  const compareData =
    compareIndices && compareIndices.length === 2
      ? [
          {
            assignment: diffs[compareIndices[0]].assignment,
            date: new Date(diffs[compareIndices[0]].createdAt),
          },
          {
            assignment: diffs[compareIndices[1]].assignment,
            date: new Date(diffs[compareIndices[1]].createdAt),
          },
        ]
      : null;

  const columns = useMemo(
    () => [
      {
        Header: "",
        id: "select",
        width: 36,
        disableSortBy: true,
        Cell: ({ row }: { row: any }) => (
          <input
            type="checkbox"
            checked={selectedRows.includes(row.index)}
            onChange={() => handleCheckboxChange(row.index)}
            aria-label="Select row for comparison"
          />
        ),
      },
      {
        Header: "",
        id: "view",
        Cell: ({ row }: { row: any }) => (
          <button
            type="button"
            className="p-1 text-gray-500 hover:text-indigo-600"
            title="View full assignment"
            onClick={(e) => {
              e.stopPropagation();
              openModal(
                row.original.assignment,
                row.original.createdAt
                  ? new Date(row.original.createdAt)
                  : undefined
              );
            }}
          >
            <EyeIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        ),
        width: 36,
        disableSortBy: true,
      },
      {
        Header: "Event Date",
        accessor: "createdAt",
        Cell: ({ value }: { value: string | number | Date }) => (
          <span>{new Date(value).toLocaleString()}</span>
        ),
        align: "left",
      },
      {
        Header: "Action",
        accessor: "action",
        Cell: ({ value }: { value: string }) => <span>{value}</span>,
        align: "left",
      },
      {
        Header: "Assignment Changes",
        accessor: "assignment",
        Cell: ({ row }: { row: any }) => (
          <AssignmentDiffViewer
            prev={prevAssignments[row.index]}
            curr={row.original.assignment}
          />
        ),
        align: "left",
      },
    ],
    [prevAssignments, selectedRows]
  );

  return (
    <div>
      <div className="flex items-center justify-between py-2">
        <h2 className="text-lg font-semibold text-gray-900">
          Assignment Diffs
        </h2>
        <button
          type="button"
          className={`flex items-center gap-2 rounded px-3 py-1 text-sm font-medium ${
            compareData
              ? "bg-indigo-600 text-white hover:bg-indigo-700"
              : "cursor-not-allowed bg-gray-200 text-gray-500"
          }`}
          disabled={!compareData}
          onClick={() => setCompareModalOpen(true)}
        >
          <SwitchHorizontalIcon className="h-5 w-5" />
          Compare
        </button>
      </div>

      <DeltaMathTable data={diffs} columns={columns} />
      <AssignmentFullViewModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        assignment={modalAssignment}
        eventDate={modalDate}
        width="50vw"
        height="75vh"
      />
      {compareData && (
        <AssignmentCompareModal
          open={compareModalOpen}
          onClose={() => setCompareModalOpen(false)}
          assignments={compareData}
        />
      )}
    </div>
  );
};

export default AssignmentHistoryDiffs;
